/* panel-splitter.tsx generated by @compiled/babel-plugin v0.29.1 */
import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import "./panel-splitter.compiled.css";
import * as React from 'react';
import { ax, ix } from "@compiled/react/runtime";
import { createContext, Fragment, useContext, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import invariant from 'tiny-invariant';
import { draggable } from '@atlaskit/pragmatic-drag-and-drop/element/adapter';
import { disableNativeDragPreview } from '@atlaskit/pragmatic-drag-and-drop/element/disable-native-drag-preview';
import { preventUnhandled } from '@atlaskit/pragmatic-drag-and-drop/prevent-unhandled';
import { Pressable } from '@atlaskit/primitives';
import VisuallyHidden from '@atlaskit/visually-hidden';
import { useTextDirection } from './use-text-direction';
import { getComputedWidth, getWidthFromDragLocation } from './utils';
var styles = {
  root: "_1e0cglyw _kqswstnw _u7coidpf _152tidpf _rjxpt94y _12ji1r31 _1qu2glyw _12y31o36 _181n1ule",
  grabArea: "_1bsb7vkz _4t3i1osq _1yt4ze3t _syaz1j28 _bfhk18uv _v5645ge5 _1lww5cps _kqswstnw _d0aluf7j _1gavfnf5 _f8pj1e6v _30l31e6v _3ivp12xp _o7ks12xp",
  line: "_1e0c1ule _1bsbjfq9 _4t3i1osq _syaz1kw7 _bfhk1r31",
  panelChildrenWrapper: "_1reo1wug _18m91wug _4t3i1osq",
  buttonContent: "_1bsb1osq _4t3i1osq"
};
var PanelSplitterContext = /*#__PURE__*/createContext(null);
/**
 * Provides the context required for the panel splitter to work within a page layout slot. Should be used in the page layout slot components, e.g. SideNav, Aside etc, as opposed to products.
 */
export var PanelSplitterProvider = function PanelSplitterProvider(_ref) {
  var panelWidth = _ref.panelWidth,
    onCompleteResize = _ref.onCompleteResize,
    resizeBounds = _ref.resizeBounds,
    resizingCssVar = _ref.resizingCssVar,
    onClick = _ref.onClick,
    panelRef = _ref.panelRef,
    children = _ref.children;
  var portalRef = useRef(null);
  return /*#__PURE__*/React.createElement(Fragment, null, /*#__PURE__*/React.createElement(PanelSplitterContext.Provider, {
    value: {
      panelWidth: panelWidth,
      onCompleteResize: onCompleteResize,
      resizeBounds: resizeBounds,
      portalRef: portalRef,
      resizingCssVar: resizingCssVar,
      onClick: onClick,
      panelRef: panelRef
    }
  }, /*#__PURE__*/React.createElement("div", {
    className: ax([styles.panelChildrenWrapper])
  }, children)), /*#__PURE__*/React.createElement("div", {
    ref: portalRef
  }));
};
var PortaledPanelSplitter = function PortaledPanelSplitter(_ref2) {
  var label = _ref2.label,
    onResizeStart = _ref2.onResizeStart,
    onResizeEnd = _ref2.onResizeEnd,
    interactionName = _ref2.interactionName;
  var splitterRef = useRef(null);
  var panelSplitterContext = useContext(PanelSplitterContext);
  invariant(panelSplitterContext, 'Panel splitter context must be set');
  var panelWidth = panelSplitterContext.panelWidth,
    onCompleteResize = panelSplitterContext.onCompleteResize,
    resizeBounds = panelSplitterContext.resizeBounds,
    panelRef = panelSplitterContext.panelRef,
    resizingCssVar = panelSplitterContext.resizingCssVar,
    portalRef = panelSplitterContext.portalRef;
  invariant(portalRef.current, 'Portal ref must be set');
  var direction = useTextDirection(portalRef.current);
  useEffect(function () {
    var splitter = splitterRef.current;
    invariant(splitter, 'Splitter ref must be set');
    return draggable({
      element: splitter,
      onGenerateDragPreview: function onGenerateDragPreview(_ref3) {
        var nativeSetDragImage = _ref3.nativeSetDragImage;
        // We will be moving the line to indicate a drag. We can disable the native drag preview
        disableNativeDragPreview({
          nativeSetDragImage: nativeSetDragImage
        });
        // We don't want any native drop animation for when the user does not drop on a drop target. We want the drag to finish immediately
        preventUnhandled.start();
      },
      getInitialData: function getInitialData() {
        invariant(panelRef.current, 'Panel ref must be set');

        /**
         * The drag calculations require the actual computed width of the element
         * For example, if the panel has loaded with a width of 2000px, but the max bound is 1000px and is visually 1000px (due to the CSS `clamp()`),
         * the drag calculations should use the actual width of 1000px, not the width in state of 2000px.
         */
        return {
          initialWidth: getComputedWidth(panelRef.current)
        };
      },
      onDragStart: function onDragStart(_ref4) {
        var source = _ref4.source;
        invariant(typeof source.data.initialWidth === 'number', 'expected initialWidth to be a number');
        onResizeStart === null || onResizeStart === void 0 || onResizeStart({
          initialWidth: source.data.initialWidth
        });
      },
      onDrag: function onDrag(_ref5) {
        var _panelRef$current;
        var location = _ref5.location,
          source = _ref5.source;
        if (typeof source.data.initialWidth !== 'number') {
          return;
        }
        (_panelRef$current = panelRef.current) === null || _panelRef$current === void 0 || _panelRef$current.style.setProperty(resizingCssVar, "clamp(".concat(resizeBounds.min, ", ").concat(getWidthFromDragLocation({
          initialWidth: source.data.initialWidth,
          location: location,
          direction: direction
        }), "px, ").concat(resizeBounds.max, ")"));
      },
      onDrop: function onDrop(_ref6) {
        var source = _ref6.source;
        preventUnhandled.stop();
        invariant(panelRef.current, 'Panel ref must be set');
        invariant(typeof source.data.initialWidth === 'number', 'expected initialWidth to be a number');
        var finalWidth = getComputedWidth(panelRef.current);
        onCompleteResize(finalWidth);
        onResizeEnd === null || onResizeEnd === void 0 || onResizeEnd({
          initialWidth: source.data.initialWidth,
          finalWidth: finalWidth
        });
      }
    });
  }, [onCompleteResize, onResizeStart, onResizeEnd, panelRef, resizingCssVar, panelWidth, resizeBounds, direction]);
  return /*#__PURE__*/createPortal( /*#__PURE__*/React.createElement("div", {
    className: ax([styles.root])
  }, /*#__PURE__*/React.createElement(Pressable
  // To win the specificity war we need to use inline styles so Compiled wins over Emotion.
  // Sorry Kylor!
  // eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop
  , {
    style: {
      outline: 'none'
    },
    ref: splitterRef,
    xcss: styles.grabArea,
    onClick: panelSplitterContext.onClick,
    interactionName: interactionName
  }, /*#__PURE__*/React.createElement("div", {
    className: ax([styles.buttonContent])
  }, /*#__PURE__*/React.createElement(VisuallyHidden, null, label), /*#__PURE__*/React.createElement("span", {
    className: ax([styles.line])
  })))), portalRef.current);
};

// Ensures that the component is only rendered on a client. Uses a `useEffect`, which is not run on servers.
var ClientOnly = function ClientOnly(_ref7) {
  var children = _ref7.children;
  var _useState = useState(false),
    _useState2 = _slicedToArray(_useState, 2),
    hasMounted = _useState2[0],
    setHasMounted = _useState2[1];
  useEffect(function () {
    setHasMounted(true);
  }, []);
  return /*#__PURE__*/React.createElement(Fragment, null, hasMounted ? children : null);
};

/**
 * _PanelSplitter_
 *
 * A component that allows the user to resize a panel.
 * It can be used within page layout slots like SideNav. The page layout component should provide the context for it,
 * using `<PanelSplitterProvider>`.
 *
 * Example usage in products:
 * ```tsx
 * <SideNav>
 *   {/* other side nav content *}
 *   <PanelSplitter label="Resize Side Nav" />
 * </SideNav>
 * ```
 */
export var PanelSplitter = function PanelSplitter(_ref8) {
  var label = _ref8.label,
    onResizeStart = _ref8.onResizeStart,
    onResizeEnd = _ref8.onResizeEnd,
    interactionName = _ref8.interactionName;
  return /*#__PURE__*/React.createElement(ClientOnly, null, /*#__PURE__*/React.createElement(PortaledPanelSplitter, {
    label: label,
    onResizeStart: onResizeStart,
    onResizeEnd: onResizeEnd,
    interactionName: interactionName
  }));
};